import React, { useState } from 'react';
import { Smartphone, Download, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import { usePwaInstallPrompt } from '@/hooks/usePwaInstallPrompt';

const KEY = 'docsite_recovery_install_later';
const IN_APP = /WhatsApp|FBAN|FBAV|Instagram|Line\/|; wv\)/i;

// Installing the recovery page as an app keeps it alive longer in the background → more reliable reminders.
export default function InstallNudge() {
  const { canInstall, promptInstall, isIOS, isAndroid, isInstalled } = usePwaInstallPrompt();
  const [later, setLater] = useState(() => Date.now() - Number(localStorage.getItem(KEY) || 0) < 3 * 864e5);

  if (isInstalled) {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2 text-[12.5px] font-semibold text-emerald-800 flex items-center gap-2" data-testid="recovery-install-done">
        <CheckCircle2 size={15} /> Recovery app installed — open it from your Home Screen for reminders.
      </div>
    );
  }
  if (later || isIOS || IN_APP.test(navigator.userAgent)) return null;

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') toast.success('Installed — open DocSite from your Home Screen');
  };
  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setLater(true); };

  return (
    <section className="rounded-2xl border-2 border-teal-300 bg-teal-50 p-4" data-testid="recovery-install-nudge">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-teal-600 text-white flex items-center justify-center shrink-0"><Smartphone size={20} /></span>
        <div className="flex-1">
          <div className="text-[14px] font-extrabold text-[#0b1f3a]">Install the recovery app for reliable reminders</div>
          <p className="text-[12.5px] text-slate-700 mt-0.5">Phones pause web pages in the background. As an app, your medicine reminders and surgeon messages arrive more reliably.</p>
        </div>
        <button type="button" onClick={dismiss} className="text-slate-400" aria-label="Later" data-testid="recovery-install-later"><X size={16} /></button>
      </div>
      {canInstall ? (
        <button type="button" onClick={install} className="mt-3 w-full py-3 rounded-xl bg-teal-600 text-white font-extrabold text-[14px] inline-flex items-center justify-center gap-2" data-testid="recovery-install-btn">
          <Download size={16} /> Install app
        </button>
      ) : (
        <p className="mt-3 text-[12.5px] font-semibold text-[#0b1f3a]" data-testid="recovery-install-manual">
          {isAndroid ? 'Tap the ⋮ menu in Chrome → “Install app” or “Add to Home screen”.' : 'Use your browser menu → “Install DocSite” to add it as an app.'}
        </p>
      )}
    </section>
  );
}
